import React, { MouseEventHandler, ReactNode } from 'react';
import {
  Badge, ListItemButton, ListItemIcon, ListItemText, Skeleton, SvgIconTypeMap,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useNavigate } from 'react-router-dom';

type NavigationItemProps = {
  name: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  Icon?: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>> & { muiName: string; };
  title: ReactNode;
  visible?: boolean;
  selected?: string;
  inverted?: boolean;
  drawerOpen?: boolean;
  badgeCount?: number;
};

function NavigationItem({
  name, onClick, Icon, title, visible, selected, inverted, drawerOpen, badgeCount = 0,
}: NavigationItemProps) {
  const navigate = useNavigate();
  if (!visible) return null;

  const onClickHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (onClick) {
      onClick(e);
    } else {
      navigate(`/${name}`);
    }
  };

  const isSelected = selected && selected.startsWith(`/${name}`);

  const sx = inverted ? {
    color: 'secondary.text',
    ...(isSelected && {
      color: 'primary.main',
      borderRight: '2px solid',
      borderColor: 'primary.main',
    }),
    paddingY: 1.25,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  } : {
    bgcolor: isSelected ? '#fff' : undefined,
    color: isSelected ? 'primary.dark' : undefined,
    '&:hover': {
      bgcolor: isSelected ? '#EEECFB' : undefined,
      color: isSelected ? 'primary.dark' : undefined,
    },
  };

  const getIconColor = () => {
    if (inverted) {
      return (isSelected ? 'primary.main' : 'secondary.text');
    }
    return (isSelected ? 'primary.dark' : 'primary.contrastText');
  };

  return (
    <ListItemButton
      onClick={onClickHandler}
      sx={sx}
    >
      <ListItemIcon>
        {
          Icon
            ? (
              <Badge
                badgeContent={badgeCount}
                overlap="circular"
                sx={{ '& .MuiBadge-badge': { bgcolor: '#d84315', color: '#fff' } }}
              >
                <Icon sx={{ color: getIconColor() }} />
              </Badge>
            )
            : <Skeleton sx={{ bgcolor: 'primary.contrastText' }} variant="circular" width={24} height={24} />
        }

      </ListItemIcon>
      <ListItemText
        primary={title}
        primaryTypographyProps={inverted ? {
          sx: {
            fontWeight: 500,
            ml: -3,
            fontSize: '15px',
            letterSpacing: 0,
            opacity: drawerOpen ? 1 : 0,
            transition: 'opacity 300ms',
          },
        } : undefined}
      />
    </ListItemButton>
  );
}

export default NavigationItem;
